import { createTheme } from "@mui/material/styles"

const getDesignTokens = (mode, scheme) => {
    let primaryStr = "#9792E3"
    let secondaryStr = "#92b5e3"

    if (scheme === "rose") {
        primaryStr = "#e75a7c"
        secondaryStr = "#9792E3"
    } else if (scheme === "mint") {
        primaryStr = "#5AE7C6"
        secondaryStr = "#9792E3"
    } else if (scheme === "sunset") {
        primaryStr = "#EE6C4D"
        secondaryStr = "#9792E3"
    }

    const alphaPrimaryHigh = primaryStr + "59" // ~35% opacity
    const alphaSecondaryHigh = secondaryStr + "59"
    const alphaPrimaryLow = primaryStr + "40" // ~25% opacity
    const alphaSecondaryLow = secondaryStr + "40"

    const bgGradient = `radial-gradient(at 0% 0%, ${alphaPrimaryHigh} 0px, transparent 80%), radial-gradient(at 100% 0%, ${alphaSecondaryHigh} 0px, transparent 80%), radial-gradient(at 100% 100%, ${alphaPrimaryLow} 0px, transparent 80%), radial-gradient(at 0% 100%, ${alphaSecondaryLow} 0px, transparent 80%)`

    const sharedPalette = {
        primary: { main: primaryStr },
        secondary: { main: secondaryStr },
        error: { main: "#FF453A" },
        info: { main: "#0A84FF" },
        success: { main: "#32D74B" },
        warning: { main: "#FF9F0A" },
    }

    const palette = {
        mode,
        ...sharedPalette,
        ...(mode === "light"
            ? {
                  background: {
                      default: "#F2F2F7",
                      paper: "#FFFFFF",
                      gradient: bgGradient,
                  },
                  text: {
                      primary: "#000000",
                      secondary: "rgba(60, 60, 67, 0.6)",
                  },
                  divider: "rgba(60, 60, 67, 0.12)",
              }
            : {
                  background: {
                      default: "#000000",
                      paper: "#1C1C1E",
                      gradient: bgGradient,
                  },
                  text: {
                      primary: "#FFFFFF",
                      secondary: "rgba(235, 235, 245, 0.6)",
                  },
                  divider: "rgba(84, 84, 88, 0.65)",
              }),
    }

    return {
        palette,
        typography: {
            fontFamily: [
                "-apple-system",
                "BlinkMacSystemFont",
                '"Segoe UI"',
                "Roboto",
                '"Helvetica Neue"',
                "Arial",
                "sans-serif",
            ].join(","),
            h1: { fontWeight: 700, letterSpacing: "-0.02em" },
            h2: { fontWeight: 700, letterSpacing: "-0.01em" },
            h3: { fontWeight: 700 },
            h4: { fontWeight: 600 },
            h5: { fontWeight: 600 },
            h6: { fontWeight: 600 },
            button: { fontWeight: 600, textTransform: "none" },
        },
        shape: {
            borderRadius: 14,
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        padding: "10px 20px",
                        boxShadow: "none",
                        "&:hover": {
                            boxShadow: "none",
                        },
                    },
                    contained: {
                        "&:active": {
                            boxShadow: "none",
                            opacity: 0.8,
                        },
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 16,
                        boxShadow:
                            mode === "light"
                                ? "0px 4px 20px rgba(0, 0, 0, 0.05)"
                                : "none",
                        backgroundImage: "none",
                        backgroundColor:
                            mode === "light"
                                ? "rgba(255, 255, 255, 0.65)"
                                : "rgba(28, 28, 30, 0.65)",
                        backdropFilter: "saturate(200%) blur(30px)",
                        WebkitBackdropFilter: "saturate(200%) blur(30px)",
                        border:
                            mode === "light"
                                ? "1px solid rgba(255, 255, 255, 0.4)"
                                : "1px solid rgba(255, 255, 255, 0.05)",
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: "none",
                        backgroundColor:
                            mode === "light"
                                ? "rgba(255, 255, 255, 0.65)"
                                : "rgba(28, 28, 30, 0.65)",
                        backdropFilter: "saturate(200%) blur(30px)",
                        WebkitBackdropFilter: "saturate(200%) blur(30px)",
                        border:
                            mode === "light"
                                ? "1px solid rgba(255, 255, 255, 0.4)"
                                : "1px solid rgba(255, 255, 255, 0.05)",
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor:
                            mode === "light"
                                ? "rgba(255, 255, 255, 0.65)"
                                : "rgba(28, 28, 30, 0.65)",
                        backdropFilter: "saturate(200%) blur(30px)",
                        WebkitBackdropFilter: "saturate(200%) blur(30px)",
                        boxShadow:
                            mode === "light"
                                ? "0 4px 30px rgba(0, 0, 0, 0.05)"
                                : "0 4px 30px rgba(0, 0, 0, 0.3)",
                        borderBottom:
                            mode === "light"
                                ? "1px solid rgba(255, 255, 255, 0.4)"
                                : "1px solid rgba(255, 255, 255, 0.05)",
                        backgroundImage: "none",
                        color: mode === "light" ? "#000" : "#fff",
                    },
                },
            },
            MuiBottomNavigation: {
                styleOverrides: {
                    root: {
                        backgroundColor:
                            mode === "light"
                                ? "rgba(255, 255, 255, 0.65)"
                                : "rgba(28, 28, 30, 0.65)",
                        backdropFilter: "saturate(200%) blur(30px)",
                        WebkitBackdropFilter: "saturate(200%) blur(30px)",
                        borderTop:
                            mode === "light"
                                ? "1px solid rgba(255, 255, 255, 0.4)"
                                : "1px solid rgba(255, 255, 255, 0.05)",
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: 20,
                        backgroundColor:
                            mode === "light"
                                ? "rgba(255, 255, 255, 0.65)"
                                : "rgba(28, 28, 30, 0.65)",
                        backdropFilter: "saturate(200%) blur(30px)",
                        WebkitBackdropFilter: "saturate(200%) blur(30px)",
                        border:
                            mode === "light"
                                ? "1px solid rgba(255, 255, 255, 0.4)"
                                : "1px solid rgba(255, 255, 255, 0.05)",
                        backgroundImage: "none",
                    },
                },
            },
            // MuiBackdrop: {
            //     styleOverrides: {
            //         root: {
            //             backgroundColor:
            //                 mode === "light"
            //                     ? "rgba(15, 23, 42, 0.18)"
            //                     : "rgba(2, 6, 23, 0.34)",
            //             backdropFilter: "blur(8px)",
            //             WebkitBackdropFilter: "blur(8px)",
            //         },
            //     },
            // },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 12,
                        },
                    },
                },
            },
        },
    }
}

export const getTheme = (mode, scheme) =>
    createTheme(getDesignTokens(mode, scheme))

export const darkTheme = getTheme("dark", "default")
export const lightTheme = getTheme("light", "default")
